import {Divider} from "primereact/divider";
import {useDispatch, useSelector} from "react-redux";

import AppreciationSectionIcon from "../../component/appreciation/AppreciationSectionIcon";
import CollapsibleTitleButton from "../../component/CollapsibleTitleButton";
import LoadingIndicator from "../../component/LoadingIndicator";
import ParameterInput from "../../component/paramInput/ParameterInput";
import {useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {selectVisibleObjectParametersCurried} from "../../redux/objectParametersSelectors";
import {selectSingleMetaParam} from "../../redux/objectParametersSlice";
import {updateParamAndValidateAndSubmitFormThunk} from "../../redux/objectParametersThunk";
import {selectPersistenceLoading} from "../../redux/persistenceSlice";
import {selectLoading as selectSpecLoading} from "../../redux/specSlice";

/**
 * Title for an input/output section in the object page.
 * The appreciation icon is rendered, only if appreciationTab and appreciationSection is defined.
 * @param {string} title title to display
 * @param {string|null} [appreciationTab] optional tab for appreciation icon
 * @param {string|null} [appreciationSection] optional section for appreciation icon
 * @param {string|null} [classNameMarginTop] optional className for the margin top of the section
 * @param {boolean|null} [firstSection=false] optional className for the margin top of the section
 * @param {string|null} [collapseKey] optional: when provided, show collapse/expand icon and make header a clickable button
 * @return {JSX.Element}
 */
export function SectionTitle({
    title,
    appreciationTab,
    appreciationSection,
    classNameMarginTop,
    firstSection = false,
    collapseKey,
}) {
    const dispatch = useDispatch();
    const collapseMetaParam = collapseKey ? `collapse_section_${collapseKey}` : null;
    const collapsedState = useSelector((state) => selectSingleMetaParam(state, collapseMetaParam ?? ""));

    const collapsable = !!collapseKey;
    const toggleCollapsedState = () => {
        if (collapsable) {
            dispatch(updateParamAndValidateAndSubmitFormThunk(collapseMetaParam, "META", !collapsedState, null));
        }
    };

    const effectiveClassNameMarginTop = classNameMarginTop ? classNameMarginTop : firstSection ? "mt-0" : "mt-6";

    const appreciationIconVisible = appreciationTab && appreciationSection;
    const appreciation = appreciationIconVisible
        ? {page: "object", tab: appreciationTab, section: appreciationSection}
        : undefined;

    return (
        <h3
            className={`${effectiveClassNameMarginTop} relative mb-4 pt-0 pb-2 border-b border-gray-300 flex flex-row items-center title-font-size`}
        >
            {collapsable ? (
                <CollapsibleTitleButton
                    label={title}
                    isExpanded={!collapsedState}
                    onToggle={toggleCollapsedState}
                    appreciation={appreciation}
                    className={`section-toggle-button !p-0 m-0 ${appreciationIconVisible ? "mr-4" : ""} flex-1`}
                />
            ) : (
                <>
                    <span className="grow">{title}</span>
                    {appreciationIconVisible && (
                        <AppreciationSectionIcon page="object" tab={appreciationTab} section={appreciationSection} />
                    )}
                </>
            )}
        </h3>
    );
}

/**
 * Section content which can be collapsed by a collapsable SectionTitle.
 * @param {string} collapseKey must be the same value as for the collapsable SectionTitle above it
 * @param {JSX.Element} children inner JSX elements
 */
export function CollapsableSection({collapseKey, children}) {
    const collapseMetaParam = collapseKey ? `collapse_section_${collapseKey}` : null;
    const collapsedState = useSelector((state) => selectSingleMetaParam(state, collapseMetaParam ?? ""));

    if (collapsedState) return null;

    return children;
}

export function PanelTitle({title}) {
    if (!title) return null;

    return (
        <>
            <h4 className="my-0">{title}</h4>
            <Divider />
        </>
    );
}

/**
 * @param {RevaSpecObjectInputRow} row
 * @param {string|null} [suggestionPlaceholder] optionaler Vorschlag als Platzhalter des leeren Felds (#19518)
 * @return {JSX.Element}
 */
export function ObjectInputsRow({row, page, suggestionPlaceholder}) {
    const selectByCurrentLanguage = useSelectByCurrentLanguage();
    const input = (
        <ParameterInput
            parameter={row.parameter}
            parameterType={row.parameterType}
            parameter_details_def={row.details}
            label={selectByCurrentLanguage(row.nameOverrideDe, row.nameOverrideEn)}
            associatedOutputApiParam={row.associatedOutputApiParam}
            ranges={row.ranges}
            page={page}
            suggestionPlaceholder={suggestionPlaceholder}
        />
    );
    if (row.lineAbove) {
        return (
            <>
                <Divider />
                {input}
            </>
        );
    } else {
        return input;
    }
}

/**
 * @param {RevaSpecObjectInputBlock} block
 * @param {string} [id]
 * @param {Record<string, string>} [suggestionPlaceholders] Vorschläge je Parameter, die nur als Platzhalter
 *        angezeigt und nie gespeichert werden (#19518)
 * @return {JSX.Element}
 */
export function ObjectInputsBlock({block, page, id, suggestionPlaceholders}) {
    const selectByCurrentLanguage = useSelectByCurrentLanguage();
    return (
        <div className="rounded-border bg-surface-100 dark:bg-surface-700 p-4 panelForInputs" id={id}>
            <PanelTitle title={selectByCurrentLanguage(block.titleDe, block.titleEn)} />
            {block.rows.map((row, index) => (
                <ObjectInputsRow
                    key={`${row.parameter}_${index}`}
                    row={row}
                    page={page}
                    suggestionPlaceholder={suggestionPlaceholders?.[row.parameter]}
                />
            ))}
        </div>
    );
}

/**
 * @param {RevaSpecObjectInputColumn} column
 * @param {{tab: string, section: string}|null} [appreciation] appreciation for column title
 * @return {JSX.Element}
 */
function ObjectInputsColumn({column, appreciation, page}) {
    const selectByCurrentLanguage = useSelectByCurrentLanguage();
    return (
        <>
            <SectionTitle
                title={selectByCurrentLanguage(column.titleDe, column.titleEn)}
                firstSection={true}
                appreciationTab={appreciation?.tab}
                appreciationSection={appreciation?.section}
            />
            {column.blocks.map((block) => (
                <ObjectInputsBlock key={block.key} block={block} page={page} />
            ))}
        </>
    );
}

/**
 * @param {string} page page of inputs to display
 * @param {ObjectDisplayStatus} displayStatus Flag, if all inputs (even hidden) should always be visible
 * @param {Object<string, {tab: string, section: string}>|null} [appreciations] appreciations for column titles
 * @return {JSX.Element}
 */
function ObjectInputsGrid({page, displayStatus, appreciations}) {
    const visibleObjectParameters = useSelector(selectVisibleObjectParametersCurried(page, displayStatus));

    if (!visibleObjectParameters.length) return null;

    // Align visible columns in grid
    const columnClassName = `col-span-${12 / visibleObjectParameters.length}`;
    return (
        <div className="grid grid-cols-12 gap-4">
            {visibleObjectParameters.map((column) => (
                <div key={column.key} className={columnClassName}>
                    <ObjectInputsColumn column={column} appreciation={appreciations?.[column.key]} page={page} />
                </div>
            ))}
        </div>
    );
}

/**
 * @param {string} [page] page of inputs to display
 * @param {ObjectDisplayStatus} [displayStatus] Flag, if all inputs (even hidden) should always be visible
 * @param {Object<string, {tab: string, section: string}>|null} [appreciations] appreciations for column titles
 * @return {JSX.Element}
 */
export default function ObjectInputs({page = "object/overview", displayStatus, appreciations}) {
    const specLoading = useSelector(selectSpecLoading);
    const persistenceLoading = useSelector(selectPersistenceLoading);

    const loading = specLoading || persistenceLoading;

    return (
        <div className="flex-1">
            <LoadingIndicator loading={loading} className="h-full" />
            {!loading && <ObjectInputsGrid page={page} displayStatus={displayStatus} appreciations={appreciations} />}
        </div>
    );
}
